import { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { v4 as uuid } from "uuid";
import { ProviderSchedulesStyled } from "./provider-schedules.styles";
import ReusableTabs from "../../../../components/tabs/tabs";
import { TimePickerField } from "../../../../components/form-fields/time-picker-field/time-picker-field";
import { SwitchField } from "../../../../components/form-fields/switch-field/switch-field";
import { PrimaryButton } from "../../../../components/button/button";
import Loader from "../../../../components/loader/loader";
import { addNotification } from "../../../../store/slices/ui-slice";
import { useAppDispatch } from "../../../../store/hooks";
import {
  getProviderSchedule,
  updateProviderSchedule,
} from "../../../../actions";
import { ProviderResponse } from "../../../../types/common-types";

// Required for dayjs(string, "HH:mm") and dayjs(string, "hh:mm A") parsing — without
// this plugin those calls silently fall back to native Date.parse which can't handle
// time-only strings and returns Invalid Date. Loading is idempotent so the TimePickerField
// and to12/to24 helpers below also work correctly.
dayjs.extend(customParseFormat);

type DayKey =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

interface DayConfig {
  enabled: boolean;
  start: string; // "HH:mm" 24-hour, Chicago standard time
  end: string;
}

type Schedule = Record<DayKey, DayConfig>;

const DAYS: { key: DayKey; label: string }[] = [
  { key: "sunday", label: "Sunday" },
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
];

// Time pickers expose values as "hh:mm A"; API speaks "HH:mm".
const to12 = (hhmm: string) => dayjs(hhmm, "HH:mm").format("hh:mm A");
const to24 = (display: string) => dayjs(display, "hh:mm A").format("HH:mm");

const DayRow = ({
  day,
  config,
  onChange,
}: {
  day: { key: DayKey; label: string };
  config: DayConfig;
  onChange: (next: DayConfig) => void;
}) => {
  return (
    <Box className={`day-row${config.enabled ? "" : " disabled"}`}>
      <Typography className="day-label">{day.label}</Typography>
      <SwitchField
        name={`${day.key}-enabled`}
        size="small"
        value={config.enabled}
        onChange={(e) => onChange({ ...config, enabled: e.target.checked })}
      />
      <TimePickerField
        label=""
        value={to12(config.start)}
        onChange={(val: string) =>
          onChange({ ...config, start: to24(val) })
        }
        disabled={!config.enabled}
      />
      <TimePickerField
        label=""
        value={to12(config.end)}
        onChange={(val: string) => onChange({ ...config, end: to24(val) })}
        disabled={!config.enabled}
      />
    </Box>
  );
};

const ProviderScheduleEditor = ({ providerId }: { providerId: string }) => {
  const dispatch = useAppDispatch();
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await getProviderSchedule(providerId);
        if (!cancelled) {
          setSchedule(response?.data?.data?.schedule as Schedule);
        }
      } catch (err: any) {
        if (!cancelled) {
          dispatch(
            addNotification({
              id: uuid(),
              type: "error",
              message:
                err?.response?.data?.message ||
                "Failed to load provider schedule.",
            }),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [providerId, dispatch]);

  const onDayChange = (day: DayKey, next: DayConfig) => {
    if (!schedule) return;
    setSchedule({ ...schedule, [day]: next });
  };

  const validationError = useMemo(() => {
    if (!schedule) return null;
    const timeRe = /^([01]\d|2[0-3]):[0-5]\d$/;
    for (const { key, label } of DAYS) {
      const cfg = schedule[key];
      if (!cfg.enabled) continue;
      if (!timeRe.test(cfg.start) || !timeRe.test(cfg.end)) {
        return `${label}: invalid time`;
      }
      if (cfg.end <= cfg.start) {
        return `${label}: end time must be after start time`;
      }
    }
    return null;
  }, [schedule]);

  const handleSave = async () => {
    if (!schedule || validationError) return;
    setSaving(true);
    try {
      await updateProviderSchedule(providerId, { schedule });
      dispatch(
        addNotification({
          id: uuid(),
          type: "success",
          message: "Provider schedule saved.",
        }),
      );
    } catch (err: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message:
            err?.response?.data?.message ||
            "Failed to save provider schedule.",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading || !schedule) {
    return <Loader absolute />;
  }

  return (
    <Box className="schedule-card">
      <Typography className="help-text">
        Times are shown in Central Time. Days marked off won't appear as
        bookable slots for this provider.
      </Typography>

      <Box className="schedule-grid">
        {DAYS.map((day) => (
          <DayRow
            key={day.key}
            day={day}
            config={schedule[day.key]}
            onChange={(next) => onDayChange(day.key, next)}
          />
        ))}
      </Box>

      {validationError && (
        <Typography color="error" sx={{ marginTop: "12px" }}>
          {validationError}
        </Typography>
      )}

      <Box className="form-actions">
        <PrimaryButton
          onClick={handleSave}
          disabled={Boolean(validationError) || saving}
          isLoading={saving}
        >
          Save
        </PrimaryButton>
      </Box>
    </Box>
  );
};

const ProviderSchedules = ({
  providersAppointmentData,
}: {
  providersAppointmentData: ProviderResponse[];
}) => {
  const tabs = useMemo(
    () =>
      (providersAppointmentData || []).map((p) => ({
        label: p.display_name,
        content: <ProviderScheduleEditor providerId={p.actor_id} />,
      })),
    [providersAppointmentData],
  );

  if (!tabs.length) {
    return (
      <ProviderSchedulesStyled>
        <Typography>No providers available.</Typography>
      </ProviderSchedulesStyled>
    );
  }

  return (
    <ProviderSchedulesStyled>
      <Box className="provider-tabs">
        <ReusableTabs tabs={tabs} />
      </Box>
    </ProviderSchedulesStyled>
  );
};

export default ProviderSchedules;
